/* Stormrock · cart drawer
 * Le rendu est fait en Liquid (snippets/sr-cart-body.liquid) et re-rendu par Horizon via le Section Rendering API.
 * Ce script ne fait que : ajouter des produits (deals, accessoires, échantillon), synchroniser les lignes cadeaux
 * avec les paliers et le produit choisi (feuille « Tes grammes offerts », attributs panier _sr_gift5 / _sr_gift10),
 * gérer la promo éclair (code + compte à rebours), les toasts et le défilement.
 */
(() => {
  if (window.SRCart) return;

  const routes = () => (window.Theme && window.Theme.routes) || {};
  const ADD = () => routes().cart_add_url || '/cart/add.js';
  const CHANGE = () => routes().cart_change_url || '/cart/change.js';
  const UPDATE = () => routes().cart_update_url || '/cart/update.js';
  const CART_JSON = () => (routes().cart_url ? routes().cart_url.replace(/\/$/, '') + '.js' : '/cart.js');
  const PROMO_KEY = 'sr_cart_promo_end';
  const GIFT_PROP = '_sr_gift';
  const REF_PROP = '_sr_ref';

  const st = {
    last: null,        // dernier état JSON (chaîne) traité
    lastObj: null,
    busy: false,
    dirty: false,
    autoFixes: 0,      // garde-fou anti-boucle
    toast: '',
    toastUntil: 0,
    toastTimer: 0,
    timer: 0,
    popKey: '',
    userAction: false,
    expiring: false,
    myKeys: new Set(),   // lignes ajoutées par ce script (toast déjà affiché)
    codes: null,         // codes promo réellement dans le panier (majuscules), null tant qu'inconnus
    codeOk: {},          // code → applicable (réponses /cart/*.js)
    codesReq: null,
    queue: [],           // ajouts demandés pendant qu'une requête est en cours (traités à la suite, sans attente côté client)
    pending: [],         // lignes optimistes en attente de la réponse serveur : { vid, info }
    sheet: { open: '', pending: '', filter: 'all' },
    alert: null,         // fenêtre « Tu vas perdre … » ouverte : { v, html, kind, codes, code, label }   // feuille « Tes grammes offerts » : cadeau ouvert (g5/g10), produit en attente, filtre
  };

  /* ---------- utilitaires ---------- */
  const q = (sel, root) => (root || document).querySelector(sel);
  const qa = (sel, root) => Array.from((root || document).querySelectorAll(sel));
  /* page panier (/cart, section sr-cart-page) en priorité, sinon le tiroir : mêmes règles, même rendu */
  const pageRoot = () => q('[data-sr-page] cart-items-component');
  const component = () => pageRoot() || q('cart-drawer-component cart-items-component');
  const drawer = () => q('cart-drawer-component');
  const cartEl = () => q('.sr-cart', component());
  const isOpen = () => !!pageRoot() || !!(q('cart-drawer-component dialog') || {}).open;
  const HOSTS = 'cart-drawer-component, [data-sr-page]';

  function readState(root) {
    const el = q('script[data-sr-state]', root || component());
    if (!el) return null;
    try {
      const s = JSON.parse(el.textContent);
      s.keys = (s.keys || []).filter(Boolean);
      return s;
    } catch (e) { return null; }
  }

  function sectionIds() {
    const ids = qa('cart-items-component').map((el) => el.dataset.sectionId).filter(Boolean);
    return Array.from(new Set(ids));
  }

  /* fetch natif (pris dans une iframe vierge) : les apps qui enrobent window.fetch
     (EasyGift, scripts d'ajout instantané) ne retiennent pas nos appels panier. */
  const nativeFetch = (() => {
    try {
      const f = document.createElement('iframe');
      f.setAttribute('aria-hidden', 'true');
      f.tabIndex = -1;
      f.style.cssText = 'display:none;width:0;height:0;border:0;position:absolute';
      (document.head || document.documentElement).appendChild(f);
      const fn = f.contentWindow.fetch;
      return (url, opts) => { try { return fn.call(window, url, opts); } catch (e) { return window.fetch(url, opts); } };
    } catch (e) {
      return (url, opts) => window.fetch(url, opts);
    }
  })();
  const TIMEOUT_MS = 15000;

  async function post(url, body) {
    const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = ctrl ? setTimeout(() => ctrl.abort(), TIMEOUT_MS) : 0;
    let res;
    try {
      res = await nativeFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
        signal: ctrl ? ctrl.signal : undefined,
      });
    } catch (e) {
      throw new Error(e && e.name === 'AbortError' ? 'Le panier met trop de temps à répondre' : 'Connexion au panier impossible');
    } finally {
      clearTimeout(timer);
    }
    let data = null;
    try { data = await res.json(); } catch (e) { data = null; }
    if (res.ok) noteCodes(data);
    if (!res.ok) {
      const err = new Error((data && (data.description || data.message)) || 'Erreur panier');
      err.data = data;
      throw err;
    }
    return data;
  }

  function withSections(body) {
    const ids = sectionIds();
    if (ids.length) {
      body.sections = ids.join(',');
      body.sections_url = window.location.pathname;
    }
    return body;
  }

  function countFrom(data) {
    const sections = data && data.sections;
    for (const id of sectionIds()) {
      const html = sections && sections[id];
      if (typeof html !== 'string') continue;
      const el = new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="cartItemCount"]');
      if (el) return parseInt(el.textContent, 10) || 0;
    }
    if (data && typeof data.item_count === 'number') return data.item_count;
    const cur = q('[ref="cartItemCount"]', component());
    return cur ? parseInt(cur.textContent, 10) || 0 : 0;
  }

  /* Diffuse la mise à jour à Horizon (morph du cart-items-component + bulle du header).
     silent : n'ouvre pas le drawer s'il est fermé. */
  function emit(data, opts) {
    const d = drawer();
    const keepClosed = !!(opts && opts.silent && d && d.hasAttribute('auto-open'));
    if (keepClosed) d.removeAttribute('auto-open');
    try {
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: null,
          sourceId: 'sr-cart',
          data: { source: 'sr-cart', sections: (data && data.sections) || null, itemCount: countFrom(data) },
        },
      }));
    } finally {
      if (keepClosed) d.setAttribute('auto-open', '');
    }
  }

  async function refreshSilently() {
    const ids = sectionIds();
    if (!ids.length) return;
    try {
      const res = await nativeFetch(`${window.location.pathname}?sections=${ids.join(',')}`, { headers: { Accept: 'application/json' } });
      emit({ sections: await res.json() }, { silent: true });
    } catch (e) { /* ignore */ }
  }

  /* ---------- toasts ---------- */
  function toast(msg, ms) {
    const dur = ms || 2600;
    st.toast = msg;
    st.toastUntil = Date.now() + dur;
    renderToast();
    clearTimeout(st.toastTimer);
    st.toastTimer = setTimeout(() => { st.toast = ''; renderToast(); }, dur + 20);
  }

  function renderToast() {
    const slot = q('[data-sr-toast]', component());
    if (!slot) return;
    const active = st.toast && Date.now() <= st.toastUntil;
    if (!active) {
      if (slot.firstChild) slot.replaceChildren();
      slot.dataset.msg = '';
      return;
    }
    if (slot.firstChild && slot.dataset.msg === st.toast) return;
    slot.dataset.msg = st.toast;
    slot.innerHTML = '<div class="toast pop" role="status"><span class="ck"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#0D0D0D" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg></span></div>';
    slot.firstChild.appendChild(document.createTextNode(st.toast));
  }

  /* ---------- promo éclair ---------- */
  function promoEnd() {
    try { return parseInt(localStorage.getItem(PROMO_KEY) || '0', 10) || 0; } catch (e) { return 0; }
  }
  function setPromoEnd(v) {
    try { v ? localStorage.setItem(PROMO_KEY, String(v)) : localStorage.removeItem(PROMO_KEY); } catch (e) { /* ignore */ }
  }
  function fmtLeft(ms) {
    const s = Math.max(0, Math.round(ms / 1000));
    const m = Math.floor(s / 60);
    const r = s % 60;
    return m + ':' + (r < 10 ? '0' : '') + r;
  }
  function tick() {
    const el = q('[data-sr-timer]', component());
    if (!el) return;
    const end = promoEnd();
    const txt = end ? fmtLeft(end - Date.now()) : '--:--';
    if (el.textContent !== txt) el.textContent = txt;
    if (end && end - Date.now() <= 0) expirePromo();
  }
  function startTicker() {
    if (!st.timer) st.timer = setInterval(tick, 1000);
  }
  function othersCsv(s) {
    return (s.promo.others || '').split(',').map((c) => c.trim()).filter(Boolean);
  }
  /* ---------- codes promo réellement dans le panier ----------
     Le Liquid (et donc les pastilles d'Horizon) ne voit que les codes « niveau panier » : un code livraison
     (VIPGOLD), un code produit ou un code valide mais pas encore applicable (minimum non atteint) est invisible,
     et le composant natif le retirerait en reconstruisant la liste à partir des pastilles. On garde donc la
     liste exacte (réponses /cart/*.js, événement discount:update, ou GET /cart.js) et on ajoute des pastilles
     masquées pour les codes manquants : Horizon les conserve alors quand le client ajoute ou retire un code. */
  function noteCodes(data) {
    if (!data || !Array.isArray(data.discount_codes)) return false;
    st.codeOk = {};
    data.discount_codes.forEach((d) => { const c = String((d && d.code) || '').toUpperCase(); if (c) st.codeOk[c] = d.applicable !== false; });
    st.codes = Object.keys(st.codeOk);
    ensurePills();
    return true;
  }
  async function fetchCodes() {
    if (st.codesReq) return st.codesReq;
    st.codesReq = (async () => {
      try {
        const res = await nativeFetch(CART_JSON(), { headers: { Accept: 'application/json' } });
        const data = res && res.ok ? await res.json() : null;
        return noteCodes(data) ? st.codes : null;
      } catch (e) { return null; } finally { st.codesReq = null; }
    })();
    return st.codesReq;
  }
  const X_SVG = '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
  /* Codes présents dans le panier mais sans remise visible ici (livraison, minimum pas atteint) : une ligne
     « au paiement » avec sa croix, portée par un li.cart-discount__pill pour qu'Horizon garde le code. */
  function ensurePills() {
    const ul = q('cart-discount-component .pills', component());
    if (!ul || !Array.isArray(st.codes)) return;
    const have = qa('.cart-discount__pill', ul).map((li) => String(li.dataset.discountCode || '').toUpperCase());
    qa('[data-sr-ghost]', ul).forEach((li) => { if (!st.codes.includes(String(li.dataset.discountCode || '').toUpperCase())) li.remove(); });
    st.codes.forEach((code) => {
      if (have.includes(code)) return;
      const ok = st.codeOk[code] !== false;
      const li = document.createElement('li');
      li.className = 'rw dsc ghost cart-discount__pill';
      li.dataset.discountCode = code;
      li.dataset.srGhost = '1';
      li.innerHTML = '<span></span><b>' + (ok ? 'Au paiement' : 'Non applicable') + '<button type="button" class="pillx" data-sr-action="code-remove">' + X_SVG + '</button></b>';
      li.firstChild.textContent = code + (ok ? ' · livraison ou remise appliquée au paiement' : ' · conditions pas encore remplies');
      li.lastChild.lastChild.setAttribute('aria-label', 'Retirer le code ' + code);
      ul.appendChild(li);
    });
  }
  async function otherCodes(s) {
    const promo = String(s.promo.code || '').toUpperCase();
    const live = await fetchCodes();
    const codes = live || othersCsv(s).map((c) => c.toUpperCase());
    return Array.from(new Set(codes.filter((c) => c && c !== promo)));
  }
  async function activatePromo(btn) {
    const s = readState();
    if (!s || s.promo.on || st.busy) return;
    setBusy(true, btn);
    try {
      const others = await otherCodes(s);
      const r = await guardApply(s, { kind: 'promo', code: s.promo.code, prev: others, codes: others.concat([s.promo.code]) });
      if (r === 'bad') toast('La promo éclair ne peut pas s’appliquer à ce panier');
      else if (r === 'ok') promoStarted(s);
    } catch (e) {
      toast(e.message || 'Impossible d’activer la promo');
    } finally {
      setBusy(false, btn);
    }
  }
  function promoStarted(s) {
    setPromoEnd(Date.now() + (s.promo.minutes || 5) * 60 * 1000);
    toast('Promo éclair -' + (s.promo.pct || 5) + ' % activée');
    startTicker();
  }

  /* ---------- garde des paliers : promo éclair et codes promo ----------
     Les paliers se calculent sur le montant après remise. Avant d'appliquer une remise, on la teste côté serveur
     (le montant réel dépend du code : produit, commande, minimum…). Si elle fait repasser le panier sous un palier
     déjà débloqué, on remet les codes d'avant et on affiche « Tu vas perdre … » : le client choisit en connaissance
     de cause (garder ses cadeaux, ajouter un deal pour tout garder, ou appliquer quand même). */
  const TRUCK_SVG = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0D0D0D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 7h11v9H3zM14 10h4l3 3v3h-7z"/><circle cx="7" cy="17.5" r="1.8"/><circle cx="17.5" cy="17.5" r="1.8"/></svg>';
  const WARN_SVG = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0D0D0D" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 8v5"/><path d="M12 16.5v.5"/></svg>';
  const CLOSE_SVG = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0D0D0D" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
  const moneyNb = (cents) => money(cents).replace(/\s€$/, '\u00a0€');
  const euros = (cents) => moneyNb(cents).replace(/,00(?=\u00a0€$)/, '');
  function lostTiers(s, newS) {
    const out = [];
    const t = s.tiers || [];
    if (t[0] && s.subtotal >= t[0] && newS < t[0]) out.push({ k: 'ship', at: t[0], name: 'Livraison offerte', val: s.ship || 0, img: '' });
    (s.gifts || []).forEach((g) => {
      if ((g.on || g.key) && g.at && s.subtotal >= g.at && newS < g.at) out.push({ k: g.k, at: g.at, name: g.name || (g.k === 'goodies' ? 'Pack goodies' : '+' + gramsOf(g.k) + ' g offerts'), val: g.val || 0, img: g.img || '' });
    });
    return out;
  }
  function codeLabel(data, code, kind, s) {
    if (kind === 'promo') return 'Promo éclair -' + (s.promo.pct || 5) + ' %';
    const C = String(code).toUpperCase();
    /* deux formes selon la route : { title, value… } ou { amount, discount_application: { title, value… } } */
    let app = (data.cart_level_discount_applications || []).map((d) => (d && d.discount_application) || d).find((d) => d && String(d.title || '').toUpperCase() === C);
    if (!app) {
      for (const it of data.items || []) {
        const al = (it.line_level_discount_allocations || []).find((x) => x.discount_application && String(x.discount_application.title || '').toUpperCase() === C);
        if (al) { app = al.discount_application; break; }
      }
    }
    const pct = app && app.value_type === 'percentage' ? Math.round(parseFloat(app.value)) : 0;
    return C + (pct ? ' · -' + pct + ' %' : '');
  }
  function rescueRow(s, lost, newS, gross) {
    const tpl = q('template[data-sr-rescue]', component());
    if (!tpl) return null;
    const top = Math.max.apply(null, lost.map((l) => l.at));
    const need = top - newS;
    const ratio = gross > 0 ? newS / gross : 1;
    let best = null;
    qa('.dr', tpl.content).forEach((row) => {
      const plus = q('.plus', row);
      const price = parseInt(plus && plus.dataset.srPrice, 10);
      if (!Number.isFinite(price) || Math.floor(price * ratio) < need + 1) return;
      if (!best || price < best.price) best = { row, price };
    });
    if (!best) return null;
    const row = best.row.cloneNode(true);
    const plus = q('.plus', row);
    plus.dataset.srAction = 'alert-deal';
    return { need, html: row.outerHTML, total: newS + Math.floor(best.price * ratio) };
  }
  function alertHtml(s, a) {
    const lost = a.lost;
    const one = lost.length === 1 ? lost[0] : null;
    const gifts = lost.filter((l) => l.k !== 'ship');
    const ship = lost.find((l) => l.k === 'ship');
    const what = { ship: ['ta', 'livraison offerte', 'ta livraison reste offerte'], g5: ['tes', '+5 g offerts', 'tes +5 g restent offerts'], g10: ['tes', '+10 g offerts', 'tes +10 g restent offerts'], goodies: ['ton', 'pack goodies', 'ton pack goodies reste offert'] };
    const w = one ? what[one.k] : ['tes', 'cadeaux', 'tes cadeaux restent offerts'];
    const title = one ? 'Tu vas perdre ' + w[0] + ' <mark>' + w[1] + '</mark>' : 'Tu vas perdre <mark>' + lost.length + ' cadeaux</mark>';
    const via = a.kind === 'promo' ? 'la promo éclair -' + (s.promo.pct || 5) + ' %' : 'le code <b>' + esc(a.code.toUpperCase()) + '</b>';
    let why;
    if (!one) why = 'Tu repasses sous ' + lost.length + ' paliers : ces cadeaux seront retirés.';
    else if (one.k === 'ship') why = 'La livraison est offerte dès <b>' + euros(one.at) + '</b> : elle te sera facturée ' + moneyNb(one.val) + '.';
    else if (one.k === 'goodies') why = 'Le pack goodies est offert dès <b>' + euros(one.at) + '</b> : il sera retiré de ton panier.';
    else why = 'Les +' + gramsOf(one.k) + ' g sont offerts dès <b>' + euros(one.at) + '</b> : ils seront retirés de ton panier.';
    const rows = lost.map((l) => {
      const ic = l.k === 'ship' ? '<span class="ic">' + TRUCK_SVG + '</span>' : (l.img ? '<img src="' + esc(l.img) + '" alt="" width="28" height="28">' : '');
      const right = l.k === 'ship' ? 'Payante<span>' + moneyNb(l.val) + '</span>' : 'Retiré' + (l.val > 0 ? '<s>' + moneyNb(l.val) + '</s>' : '');
      return '<div class="r ls"><span class="lo">' + ic + esc(l.name) + '</span><b>' + right + '</b></div>';
    }).join('');
    const G = gifts.reduce((t, l) => t + (l.val || 0), 0);
    const parts = [];
    if (gifts.length) parts.push(G > 0 ? 'tu perds <b>' + moneyNb(G) + '</b> de cadeau' + (gifts.length > 1 ? 'x' : '') : 'tu perds ' + (gifts.length > 1 ? 'tes cadeaux' : 'ton cadeau'));
    if (ship) parts.push('tu paies <b>' + moneyNb(ship.val) + '</b> de livraison');
    const rescue = a.rescue ? '<div class="sv"><p>Garde tout : il te manque ' + moneyNb(a.rescue.need) + '<span>Ajoute ce deal : ' + (a.kind === 'promo' ? 'la promo s’applique' : 'le code s’applique') + ' et ' + w[2] + ' (' + moneyNb(a.rescue.total) + ').</span></p>' + a.rescue.html + '</div>' : '';
    const keep = one ? { ship: 'ma <em>livraison offerte</em>', g5: 'mes <em>+5 g offerts</em>', g10: 'mes <em>+10 g offerts</em>', goodies: 'mon <em>pack goodies</em>' }[one.k] : 'mes <em>cadeaux</em>';
    return '<div class="hdl"></div>'
      + '<div class="hx"><span class="wi">' + WARN_SVG + '</span><button type="button" class="x" data-sr-action="alert-keep" aria-label="Fermer">' + CLOSE_SVG + '</button></div>'
      + '<h3 id="sr-alert-title-' + esc(s.section) + '">' + title + '</h3>'
      + '<p class="lead">Avec ' + via + ', ton panier passe à <b>' + moneyNb(a.newS) + '</b>. ' + why + '</p>'
      + '<div class="bx"><div class="r"><span>' + esc(a.label) + '</span><b>-' + moneyNb(a.rem) + '</b></div>' + rows
      + '<div class="r tt"><span>Nouveau total</span><b><s>' + moneyNb(s.subtotal) + '</s>' + moneyNb(a.newS) + '</b></div></div>'
      + '<p class="vd">Tu économises <b>' + moneyNb(a.rem) + '</b>, mais ' + parts.join(' et ') + '.</p>'
      + rescue
      + '<button type="button" class="bt" data-sr-action="alert-keep">Garder ' + keep + '</button>'
      + '<button type="button" class="b2" data-sr-action="alert-apply">' + (a.kind === 'promo' ? 'Activer la promo quand même' : 'Appliquer le code quand même') + '</button>';
  }
  /* Renvoie 'ok' (appliqué et rendu), 'bad' (code refusé, codes d'avant remis) ou 'held' (alerte ouverte). */
  async function guardApply(s, a) {
    const C = String(a.code).toUpperCase();
    const data = await post(UPDATE(), withSections({ discount: a.codes.join(',') }));
    const dc = Array.isArray(data.discount_codes) ? data.discount_codes : [];
    const mine = dc.find((d) => String(d.code).toUpperCase() === C);
    if (!mine || mine.applicable === false) {
      await post(UPDATE(), { discount: a.prev.join(',') });
      return 'bad';
    }
    const newS = typeof data.total_price === 'number' ? data.total_price : s.subtotal;
    const lost = lostTiers(s, newS);
    if (!lost.length) { st.userAction = true; emit(data); return 'ok'; }
    /* la réponse de /cart/update.js ne détaille pas toujours les remises : on relit /cart.js pour le « -X % » */
    let detail = data;
    if (a.kind === 'code' && !Array.isArray(data.cart_level_discount_applications)) {
      try { const r = await nativeFetch(CART_JSON(), { headers: { Accept: 'application/json' } }); if (r.ok) detail = await r.json(); } catch (e) { /* ignore */ }
    }
    await post(UPDATE(), { discount: a.prev.join(',') });
    const gross = typeof data.items_subtotal_price === 'number' ? data.items_subtotal_price : newS;
    const al = { v: Date.now(), kind: a.kind, code: a.code, codes: a.codes, prev: a.prev, lost, newS, rem: Math.max(0, s.subtotal - newS), label: codeLabel(detail, a.code, a.kind, s) };
    al.rescue = rescueRow(s, lost, newS, gross);
    al.html = alertHtml(s, al);
    st.alert = al;
    renderAlert(true);
    return 'held';
  }
  function renderAlert(focus) {
    const c = component();
    const el = c && q('[data-sr-alert]', c);
    const ov = c && q('[data-sr-alert-ov]', c);
    if (!el || !ov) return;
    if (!st.alert) {
      if (!el.hidden) { el.hidden = true; ov.hidden = true; el.innerHTML = ''; }
      return;
    }
    if (el.hidden || el.dataset.v !== String(st.alert.v)) {
      el.innerHTML = st.alert.html;
      el.dataset.v = String(st.alert.v);
      el.hidden = false;
      ov.hidden = false;
      if (focus) {
        if (canAnimate(el)) { el.animate([{ transform: 'translateY(40px)', opacity: 0 }, { transform: 'none', opacity: 1 }], { duration: 240, easing: EASE }); ov.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200 }); }
        const b = q('.bt', el);
        if (b) { try { b.focus({ preventScroll: true }); } catch (e) { /* ignore */ } }
      }
    }
  }
  function closeAlert() {
    if (!st.alert) return;
    const a = st.alert;
    st.alert = null;
    renderAlert();
    if (a.kind === 'code') { const inp = q('cart-discount-component input[name="discount"]', component()); if (inp) inp.value = ''; }
  }
  async function alertApply(btn, dealVariant, dealBtn) {
    const a = st.alert;
    const s = readState();
    if (!a || !s || st.busy) return;
    setBusy(true, btn);
    st.userAction = true;
    try {
      let name = '';
      if (dealVariant) {
        const added = await post(ADD(), { items: [{ id: Number(dealVariant), quantity: 1 }] });
        const it = added && added.items && added.items[0];
        if (it && it.key) st.myKeys.add(it.key);
        name = it ? String(it.product_title || '').replace(' · Deal panier', '') : '';
      }
      const data = await post(UPDATE(), withSections({ discount: a.codes.join(',') }));
      st.alert = null;
      renderAlert();
      if (a.kind === 'code') { const inp = q('cart-discount-component input[name="discount"]', component()); if (inp) inp.value = ''; }
      emit(data);
      if (a.kind === 'promo') promoStarted(s);
      if (name) toast(name + ' ajouté, ' + (a.kind === 'promo' ? 'promo activée' : 'code appliqué') + ' : tes cadeaux restent offerts');
      else if (a.kind === 'code') toast('Code ' + String(a.code).toUpperCase() + ' appliqué');
    } catch (e) {
      toast(e.message || 'Impossible d’appliquer la remise');
      if (dealBtn) dealBtn.disabled = false;
    } finally {
      setBusy(false, btn);
    }
  }
  /* Champ « code promo » : on passe avant le composant Horizon (écouteur en capture) pour tester le code. */
  async function applyCode(form) {
    const input = q('input[name="discount"]', form);
    const code = input ? input.value.trim() : '';
    const btn = q('button[type="submit"]', form);
    if (!code || st.busy) return;
    const cd = form.closest('cart-discount-component');
    const ref = (n) => q('[ref="' + n + '"]', cd);
    ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'].forEach((n) => { const el = ref(n); if (el) el.classList.add('hidden'); });
    const s = readState();
    if (!s) return;
    const live = await fetchCodes();
    const prev = live || qa('.cart-discount__pill', cd).map((li) => String(li.dataset.discountCode || '').toUpperCase()).filter(Boolean);
    if (prev.includes(code.toUpperCase())) { input.value = ''; return; }
    setBusy(true, btn);
    try {
      const r = await guardApply(s, { kind: 'code', code, prev, codes: prev.concat([code]) });
      if (r === 'bad') {
        input.value = '';
        ['cartDiscountError', 'cartDiscountErrorDiscountCode'].forEach((n) => { const el = ref(n); if (el) el.classList.remove('hidden'); });
      } else if (r === 'ok') {
        input.value = '';
        toast('Code ' + code.toUpperCase() + ' appliqué');
      }
    } catch (e) {
      toast(e.message || 'Impossible d’appliquer ce code');
    } finally {
      setBusy(false, btn);
    }
  }
  async function expirePromo() {
    if (st.expiring) return;
    const s = readState();
    setPromoEnd(0);
    if (!s || !s.promo.on) return;
    st.expiring = true;
    try {
      const others = await otherCodes(s);
      const data = await post(UPDATE(), withSections({ discount: others.join(',') }));
      toast('La promo éclair est terminée');
      emit(data, { silent: true });
    } catch (e) { /* ignore */ } finally { st.expiring = false; }
  }
  function syncPromo(s) {
    const end = promoEnd();
    if (s.promo.on) {
      if (!end) setPromoEnd(Date.now() + (s.promo.minutes || 5) * 60 * 1000);
      else if (end <= Date.now()) { expirePromo(); return; }
      startTicker();
      tick();
    } else if (end) {
      setPromoEnd(0);
    }
  }

  /* ---------- actions ---------- */
  function setBusy(on, btn) {
    st.busy = on;
    const c = cartEl();
    if (c) c.classList.toggle('sr-busy', on);
    if (btn) btn.disabled = on;
    if (!on && (st.dirty || st.queue.length)) { st.dirty = false; schedule(); }
  }

  /* ---------- animations ----------
     Tout est immédiat au toucher : la photo s'envole vers le compteur, le bouton passe au ✓, la ligne apparaît en tête
     et le compteur avance avant même la réponse du serveur. Les animations de déblocage utilisent l'API Web Animations
     (et non des classes CSS) pour survivre aux re-rendus d'Horizon qui patchent le DOM. */
  const reduceMotion = () => { try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; } };
  const EASE = 'cubic-bezier(.2,.7,.2,1)';
  const CHECK_SVG = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0D0D0D" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';
  function canAnimate(el) { return !!el && typeof el.animate === 'function' && !reduceMotion(); }
  function flyToCart(btn) {
    const row = btn && btn.closest('.dr, .ac');
    const img = row && q('img', row);
    const c = component();
    if (!row || !img || !c) return;
    if (!canAnimate(img)) return;
    row.animate([{ transform: 'translateY(0)' }, { transform: 'translateY(-8px)', offset: 0.4 }, { transform: 'translateY(0)' }], { duration: 420, easing: EASE });
    const from = img.getBoundingClientRect();
    const target = q('.hd .n', c) || q('[data-sr-scroll]', c);
    if (!target || !from.width) return;
    const to = target.getBoundingClientRect();
    const clone = img.cloneNode(true);
    clone.className = 'sr-fly';
    clone.style.cssText = 'left:' + from.left + 'px;top:' + from.top + 'px;width:' + from.width + 'px;height:' + from.height + 'px';
    document.body.appendChild(clone);
    const dx = (to.left + to.width / 2) - (from.left + from.width / 2);
    const dy = (to.top + to.height / 2) - (from.top + from.height / 2);
    const anim = clone.animate([
      { transform: 'translate(0,0) scale(1)', opacity: 1 },
      { transform: 'translate(' + (dx * 0.4) + 'px,' + (dy * 0.55) + 'px) scale(.75)', opacity: 1, offset: 0.55 },
      { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(.2)', opacity: 0.1 },
    ], { duration: 560, easing: EASE });
    anim.onfinish = anim.oncancel = () => clone.remove();
    setTimeout(() => { if (clone.isConnected) clone.remove(); }, 900);
  }
  function markAdded(btn) {
    if (!btn || btn.classList.contains('ok')) return;
    btn.classList.add('ok');
    btn.innerHTML = CHECK_SVG;
    if (canAnimate(btn)) btn.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.25)', offset: 0.4 }, { transform: 'scale(1)' }], { duration: 360, easing: EASE });
  }
  function bumpCount() {
    const n = q('.hd .n', component());
    if (!n || !canAnimate(n)) return;
    n.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.25)', offset: 0.35 }, { transform: 'scale(1)' }], { duration: 420, easing: 'ease-out' });
  }
  /* compteur = valeur serveur (état du dernier rendu) + ajouts en attente ; on n'écrit que si ça change (l'observateur
     de mutations relance afterRender) */
  function refreshCount(c, s) {
    const el = q('.hd .n', c);
    const base = s ? s.count : parseInt(el ? el.textContent : '0', 10) || 0;
    const n = base + st.pending.reduce((a, p) => a + (p.qty || 1), 0);
    if (el && el.textContent !== String(n)) el.textContent = String(n);
  }
  /* Palier franchi : flash jaune de la ligne cadeau, tampon « Offert » qui claque, valeur barrée qui monte,
     et le tiroir remonte vers les cadeaux. Lancé dès le premier rendu qui montre le palier (pas après la
     synchronisation de la ligne cadeau, qui ne fait que patcher la même ligne). */
  function celebrate(prev, s) {
    if (!prev || s.done <= prev.done) return;
    const c = component();
    const rows = c ? qa('.gf .gr', c) : [];
    let any = false;
    for (let k = prev.done + 1; k <= s.done; k += 1) {
      const row = rows[k - 1];
      if (!row) continue;
      any = true;
      if (!canAnimate(row)) continue;
      row.animate([{ background: '#F7F7F4' }, { background: '#FBDD45', offset: 0.12 }, { background: '#FEF6CE', offset: 0.55 }, { background: '#F7F7F4' }], { duration: 2600, easing: 'ease-out' });
      const stamp = q('.off .stamp', row);
      if (stamp) stamp.animate([{ transform: 'rotate(-7deg) scale(2.2)', opacity: 0 }, { transform: 'rotate(-7deg) scale(.92)', opacity: 1, offset: 0.55 }, { transform: 'rotate(-7deg) scale(1)' }], { duration: 520, easing: 'cubic-bezier(.2,.9,.3,1.4)' });
      [q('.off s', row), q('.gt', row)].forEach((el) => { if (el) el.animate([{ transform: 'translateY(6px)', opacity: 0 }, { transform: 'translateY(0)', opacity: 1 }], { duration: 420, delay: 180, easing: 'ease-out', fill: 'backwards' }); });
    }
    const sc = c && q('[data-sr-scroll]', c);
    const gf = c && q('.gf', c);
    if (any && sc && gf && !reduceMotion()) {
      const top = Math.max(0, gf.offsetTop - 120);
      try { sc.scrollTo({ top, behavior: 'smooth' }); } catch (e) { sc.scrollTop = top; }
    }
  }

  /* ---------- lignes optimistes ----------
     Une ligne provisoire en tête du panier + compteur avancé dès le toucher. Le morph d'Horizon les efface à chaque
     rendu : syncOptimistic les remet tant que la réponse serveur de cet ajout n'est pas arrivée. */
  function money(cents) {
    const [i, d] = (Math.max(0, cents) / 100).toFixed(2).split('.');
    return i.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + d + ' €';
  }
  function esc(v) { return String(v || '').replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch])); }
  function inCart(s, vid) { return !!(s && vid && s.keys.some((k) => k.indexOf(String(vid) + ':') === 0)); }
  function insertOpt(c, p) {
    const form = q('.cart-form', c);
    const info = p.info;
    if (!form || !info || q('.it.sr-opt[data-sr-opt="' + p.vid + '"]', c)) return;
    const img = info.shown || info.src;
    const price = Number.isFinite(info.cents) ? money(info.cents * (info.qty || 1)) : '';
    const emp = q('.emp', form);
    if (emp) emp.remove();
    form.insertAdjacentHTML('afterbegin', '<div class="it sr-opt" inert data-sr-opt="' + esc(p.vid) + '"><div class="im">' + (img ? '<img src="' + esc(img) + '" alt="" width="80" height="80">' : '') + '</div><div class="bd"><div class="l"><div class="nm">' + esc(info.title) + '</div>' + (info.variant ? '<div class="vr"><b>' + esc(info.variant) + '</b></div>' : '') + '<div class="st"><span></span></div></div><div class="r"><div class="pz"><b>' + price + '</b></div></div></div></div>');
    const row = form.firstElementChild;
    if (canAnimate(row)) row.animate([{ transform: 'translateY(-10px)', opacity: 0 }, { transform: 'translateY(0)', opacity: 0.6 }], { duration: 260, easing: EASE });
  }
  function syncOptimistic(c, s) {
    st.pending = st.pending.filter((p) => !p.done && !(p.settle && inCart(s, p.vid)));
    qa('.it.sr-opt', c).forEach((el) => { if (!st.pending.some((p) => p.vid === el.dataset.srOpt)) el.remove(); });
    st.pending.forEach((p) => { if (!inCart(s, p.vid)) insertOpt(c, p); });
    refreshCount(c, s);
  }
  /* Appelée par sr-cart-instant.js (ajout depuis une fiche produit) : renvoie la fonction qui annule la ligne. */
  function optimistic(info) {
    const c = component();
    if (!c) return () => {};
    const vid = String(info.id || info.variantId || '');
    const p = { vid, info, qty: info.qty || 1, settle: true };
    st.pending.push(p);
    const s0 = readState(c);
    if (!inCart(s0, vid)) insertOpt(c, p);
    refreshCount(c, s0);
    bumpCount();
    st.userAction = false;
    return () => {
      if (p.done) return;
      p.done = true;
      st.pending = st.pending.filter((x) => x !== p);
      qa('.it.sr-opt[data-sr-opt="' + vid + '"]', c).forEach((el) => el.remove());
      refreshCount(c, readState(c));
      const form = q('.cart-form', c);
      if (form && !q('.it', form) && st.lastObj && !st.lastObj.count) form.insertAdjacentHTML('afterbegin', '<div class="emp"><b>Ton panier est vide</b><span>Ajoute un deal ci-dessous pour commencer.</span></div>');
    };
  }
  function rowInfo(btn) {
    const row = btn && btn.closest('.dr, .ac');
    if (!row) return null;
    const img = q('img', row);
    const nm = q('.nm', row) || q('.tx b', row);
    const vr = q('.vr b', row);
    const cents = parseInt(btn.dataset.srPrice, 10);
    return { shown: img ? (img.currentSrc || img.src) : '', title: nm ? nm.textContent.trim() : 'Produit', variant: vr ? vr.textContent.trim() : '', cents: Number.isFinite(cents) ? cents : NaN, qty: 1 };
  }
  /* Le palier que cet ajout va franchir est connu d'avance (sous-total + prix) : la ligne cadeau part dans la même
     requête, pour que tout arrive d'un coup. Si le serveur décide autrement, la synchronisation corrige. */
  function predictGifts(s, cents) {
    const out = [];
    if (!s || !Number.isFinite(cents)) return out;
    const ratio = s.gross > 0 && s.subtotal < s.gross ? s.subtotal / s.gross : 1;
    const newSub = s.subtotal + Math.floor(cents * ratio);
    for (const g of s.gifts) {
      if (!g.on && !g.key && g.ready && g.variant && g.at && newSub >= g.at) out.push({ id: Number(g.variant), quantity: 1, properties: giftProps(s, g) });
    }
    return out;
  }

  async function addVariant(variantId, props, btn, label) {
    const fromDrawer = !!(btn && btn.closest('.dk, .acc'));
    const c = component();
    const s = readState(c);
    const info = fromDrawer ? rowInfo(btn) : null;
    let p = null;
    if (fromDrawer) {
      flyToCart(btn);
      markAdded(btn);
      bumpCount();
      p = { vid: String(variantId), info, qty: 1 };
      st.pending.push(p);
      if (c) { if (!inCart(s, variantId)) insertOpt(c, p); refreshCount(c, s); }
    }
    if (st.busy) { st.queue.push([variantId, props, btn, label, p]); return; }
    await runAdd(variantId, props, btn, label, p, s, info);
  }
  async function runAdd(variantId, props, btn, label, p, s, info) {
    setBusy(true, btn);
    st.userAction = true;
    try {
      const item = { id: Number(variantId), quantity: 1 };
      if (props) item.properties = props;
      const items = [item].concat(info ? predictGifts(s || readState(), info.cents) : []);
      const data = await post(ADD(), withSections({ items }));
      const added = data && data.items && data.items[0];
      st.popKey = added ? added.key : '';
      (data && data.items || []).forEach((it) => { if (it && it.key) st.myKeys.add(it.key); });
      if (label !== false) {
        const name = label || (added && (added.product_title || '').replace(' · Deal panier', '')) || 'Produit';
        toast(name + ' ajouté au panier');
      }
      if (p) { p.done = true; st.pending = st.pending.filter((x) => x !== p); }
      emit(data);
    } catch (e) {
      if (p) {
        p.done = true;
        st.pending = st.pending.filter((x) => x !== p);
        const c = component();
        if (c) { qa('.it.sr-opt[data-sr-opt="' + p.vid + '"]', c).forEach((el) => el.remove()); refreshCount(c, readState(c)); }
      }
      if (btn) { btn.classList.remove('ok'); btn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FBDD45" stroke-width="3" stroke-linecap="round" aria-hidden="true"><path d="M5 12h14M12 5v14"/></svg>'; }
      toast(e.message || 'Ce produit n’a pas pu être ajouté');
    } finally {
      setBusy(false, btn);
    }
  }

  async function pickSample(variantId, btn) {
    const s = readState();
    if (!s || st.busy) return;
    if (s.sample.key && String(s.sample.variant) === String(variantId)) return;
    /* retour immédiat : la tuile choisie passe en « on » avant la réponse */
    qa('.tile[data-sr-action="sample"]', component()).forEach((t) => { const on = t === btn; t.classList.toggle('on', on); t.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    if (btn && canAnimate(btn)) btn.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.04)', offset: 0.4 }, { transform: 'scale(1)' }], { duration: 320, easing: EASE });
    setBusy(true, btn);
    st.userAction = true;
    try {
      if (s.sample.key) await post(CHANGE(), { id: s.sample.key, quantity: 0 });
      const data = await post(ADD(), withSections({ items: [{ id: Number(variantId), quantity: 1, properties: { [GIFT_PROP]: '1' } }] }));
      const title = (data && data.items && data.items[0] && data.items[0].variant_title) || '';
      toast('Échantillon ' + title + ' 1 g ajouté à ton colis');
      emit(data);
    } catch (e) {
      toast(e.message || 'Impossible de choisir cet échantillon');
    } finally {
      setBusy(false, btn);
    }
  }

  function scrollToDeals() {
    const sc = q('[data-sr-scroll]', component());
    const target = q('#sr-deals', component());
    if (pageRoot() && target) { try { target.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' }); } catch (e) { target.scrollIntoView(); } return; }
    if (!sc || !target) return;
    const top = Math.max(0, target.offsetTop - 12);
    try { sc.scrollTo({ top, behavior: 'smooth' }); } catch (e) { sc.scrollTop = top; }
  }

  /* ---------- synchronisation des cadeaux (lignes à 0 €) ---------- */
  function giftProps(s, g) {
    const p = { [GIFT_PROP]: '1' };
    if (g.withRef) {
      p[REF_PROP] = g.want || '';
      p['Produit'] = g.title || '';
      if (g.size) p['Taille'] = g.size;
    }
    return p;
  }
  function giftStale(g) {
    return (g.ref || '') !== (g.want || '') || (g.lsize || '') !== (g.size || '');
  }

  function plan(s) {
    const ops = [];
    for (const g of s.gifts) {
      if (g.on && !g.key) { if (g.variant) ops.push({ type: 'add', id: g.variant, properties: giftProps(s, g) }); }
      else if (g.on && g.withRef && giftStale(g)) ops.push({ type: 'change', key: g.key, quantity: 1, properties: giftProps(s, g) });
      else if (g.on && g.qty !== 1) ops.push({ type: 'change', key: g.key, quantity: 1 });
      else if (!g.on && g.key) ops.push({ type: 'change', key: g.key, quantity: 0 });
    }
    if (s.sample.key && !s.keys.length) ops.push({ type: 'change', key: s.sample.key, quantity: 0 });
    else if (s.sample.key && s.sample.qty !== 1) ops.push({ type: 'change', key: s.sample.key, quantity: 1 });
    return ops;
  }

  async function reconcile(s, silent) {
    const ops = plan(s);
    if (!ops.length) { st.autoFixes = 0; return; }
    if (st.autoFixes >= 4) { console.warn('[sr-cart] synchronisation interrompue', ops); return; }
    st.autoFixes += 1;
    setBusy(true);
    let data = null;
    try {
      for (const op of ops) {
        if (op.type === 'add') {
          data = await post(ADD(), withSections({ items: [{ id: Number(op.id), quantity: 1, properties: op.properties }] }));
        } else {
          const body = { id: op.key, quantity: op.quantity };
          if (op.properties) body.properties = op.properties;
          data = await post(CHANGE(), withSections(body));
        }
      }
      if (data) emit(data, { silent });
    } catch (e) {
      console.error('[sr-cart]', e);
      setTimeout(refreshSilently, 2500);
    } finally {
      setBusy(false);
    }
  }

  /* ---------- feuille « Tes grammes offerts » ---------- */
  function giftOf(s, k) { return (s && s.gifts || []).find((g) => g.k === k) || null; }
  function gramsOf(k) { return k === 'g10' ? 10 : 5; }

  function openSheet(k) {
    const s = readState();
    const g = giftOf(s, k);
    if (!g || !g.on || !q('[data-sr-sheet]', component())) return;
    st.sheet = { open: k, pending: g.want || '', filter: 'all' };
    renderSheet();
    const ls = q('[data-sr-sheet-list]', component());
    if (ls) ls.scrollTop = 0;
    const on = ls && q('.opt.on', ls);
    if (on) { try { on.scrollIntoView({ block: 'nearest' }); } catch (e) { /* ignore */ } }
  }
  function closeSheet() {
    if (!st.sheet.open) return;
    const k = st.sheet.open;
    st.sheet = { open: '', pending: '', filter: 'all' };
    renderSheet();
    const row = q('.gr.ch[data-sr-gift="' + k + '"]', component());
    if (row) { try { row.focus({ preventScroll: true }); } catch (e) { /* ignore */ } }
  }
  function renderSheet() {
    const c = component();
    const sheet = c && q('[data-sr-sheet]', c);
    const ov = c && q('[data-sr-ov]', c);
    if (!sheet || !ov) return;
    const k = st.sheet.open;
    const s = readState(c);
    const g = k ? giftOf(s, k) : null;
    if (!k || !g || !g.on) {
      if (!sheet.hidden) { sheet.hidden = true; ov.hidden = true; }
      if (k) st.sheet = { open: '', pending: '', filter: 'all' };
      return;
    }
    const grams = gramsOf(k);
    const title = q('[data-sr-sheet-title]', sheet);
    if (title) title.textContent = 'Tes +' + grams + ' g offerts';
    const other = q('[data-sr-sheet-other]', sheet);
    if (other) other.textContent = '+' + (grams === 5 ? 10 : 5) + ' g';
    let pendingLabel = '';
    /* seules les options dont le format (5 g ou 10 g) est en stock pour ce cadeau sont proposées */
    const fits = (o) => String(o.dataset.srFmt || '5,10').split(',').includes(String(grams));
    const opts = qa('.opt', sheet);
    if (!opts.some((o) => fits(o) && o.dataset.srHandle === st.sheet.pending)) st.sheet.pending = g.want || '';
    for (const o of opts) {
      const ok = fits(o);
      const on = ok && o.dataset.srHandle === st.sheet.pending;
      o.classList.toggle('on', on);
      o.setAttribute('aria-checked', on ? 'true' : 'false');
      if (on) pendingLabel = o.dataset.srLabel || '';
      o.hidden = !ok || (st.sheet.filter !== 'all' && o.dataset.srKind !== st.sheet.filter);
    }
    for (const grp of qa('[data-sr-grp]', sheet)) {
      let el = grp.nextElementSibling, any = false;
      while (el && !el.hasAttribute('data-sr-grp')) { if (el.classList.contains('opt') && !el.hidden) { any = true; break; } el = el.nextElementSibling; }
      grp.hidden = !any;
    }
    for (const ch of qa('.chip', sheet)) {
      const on = ch.dataset.srKind === st.sheet.filter;
      ch.classList.toggle('on', on);
      ch.setAttribute('aria-pressed', on ? 'true' : 'false');
    }
    const cta = q('[data-sr-sheet-cta]', sheet);
    if (cta) cta.textContent = '· +' + grams + ' g ' + pendingLabel;
    const btn = q('[data-sr-action="sheet-validate"]', sheet);
    if (btn) btn.disabled = !st.sheet.pending;
    if (sheet.hidden) { sheet.hidden = false; ov.hidden = false; }
  }
  async function validateSheet(btn) {
    const k = st.sheet.open;
    const s = readState();
    const g = giftOf(s, k);
    const handle = st.sheet.pending;
    if (!g || !handle || st.busy) return;
    const grams = gramsOf(k);
    if (handle === (g.want || '')) { closeSheet(); return; }
    const opt = q('.opt[data-sr-handle="' + CSS.escape(handle) + '"]', component());
    const label = opt ? (opt.dataset.srLabel || '') : '';
    setBusy(true, btn);
    st.userAction = true;
    try {
      const attrs = {}; attrs['_sr_gift' + grams] = handle;
      const data = await post(UPDATE(), withSections({ attributes: attrs }));
      closeSheet();
      toast('+' + grams + ' g ' + label + ' offerts, c’est noté');
      emit(data);
    } catch (e) {
      toast(e.message || 'Impossible de changer ce cadeau');
    } finally {
      setBusy(false, btn);
    }
  }

  /* ---------- après chaque rendu ---------- */
  function milestoneToast(prev, s) {
    if (!prev || s.done <= prev.done) return false;
    const L = s.labels || {};
    const msgs = {
      1: 'Livraison offerte débloquée',
      2: (L.g5 || '+5 g offerts') + ' débloqués',
      3: (L.g10 || '+10 g offerts') + ' débloqués',
      4: (L.goodies || 'Pack goodies') + ' offert débloqué',
    };
    toast(msgs[s.done] || 'Nouveau cadeau débloqué');
    return true;
  }

  function addedToast(prev, s) {
    if (!prev) return;
    const fresh = s.keys.filter((k) => !prev.keys.includes(k));
    if (!fresh.length) return;
    const key = fresh[fresh.length - 1];
    if (!st.popKey) st.popKey = key;
    if (st.myKeys.has(key)) return;
    if (!st.userAction) {
      const row = q('.it[data-key="' + CSS.escape(key) + '"]', component());
      const nm = row && q('.nm', row);
      toast(((nm && nm.textContent) || 'Produit').trim() + ' ajouté au panier');
    }
  }

  function afterRender() {
    const c = component();
    if (!c) return;
    const s = readState(c);
    if (!s) return;
    const str = JSON.stringify(s);
    renderToast();
    ensurePills();
    renderAlert();
    syncOptimistic(c, s);
    if (str !== st.last) {
      const prev = st.lastObj;
      st.last = str;
      st.lastObj = s;
      if (!milestoneToast(prev, s)) addedToast(prev, s);
      celebrate(prev, s);
      st.userAction = false;
      if (st.popKey) {
        const row = q('.it[data-key="' + CSS.escape(st.popKey) + '"]', c);
        if (row) row.classList.add('pop');
        st.popKey = '';
      }
      syncPromo(s);
      renderSheet();
    }
    if (st.busy) { st.dirty = true; return; }
    if (plan(s).length) { reconcile(s, !isOpen()); return; }
    st.autoFixes = 0;
    if (st.queue.length) {
      const a = st.queue.shift();
      runAdd(a[0], a[1], a[2], a[3], a[4], null, a[4] && a[4].info);
    }
  }

  let schedTimer = 0;
  function schedule() { clearTimeout(schedTimer); schedTimer = setTimeout(afterRender, 60); }

  /* ---------- écouteurs ---------- */
  document.addEventListener('click', (ev) => {
    const btn = ev.target instanceof Element ? ev.target.closest('[data-sr-action]') : null;
    if (!btn || !btn.closest(HOSTS)) return;
    const action = btn.dataset.srAction;
    if (action === 'scroll-deals') { ev.preventDefault(); scrollToDeals(); return; }
    if (action === 'code-remove') {
      /* ligne « au paiement » ajoutée par ce script : on passe par le composant Horizon pour retirer le code */
      const cd = btn.closest('cart-discount-component');
      if (cd && typeof cd.removeDiscount === 'function') { btn.closest('li').style.opacity = '.4'; cd.removeDiscount(ev); }
      return;
    }
    if (btn.disabled) return;
    st.autoFixes = 0;
    if (action === 'add') { ev.preventDefault(); addVariant(btn.dataset.srVariant, null, btn); }
    else if (action === 'sample') { ev.preventDefault(); pickSample(btn.dataset.srVariant, btn); }
    else if (action === 'promo') { ev.preventDefault(); activatePromo(btn); }
    else if (action === 'choose') { ev.preventDefault(); openSheet(btn.dataset.srGift); }
    else if (action === 'sheet-close') { ev.preventDefault(); closeSheet(); }
    else if (action === 'sheet-filter') { ev.preventDefault(); st.sheet.filter = btn.dataset.srKind || 'all'; renderSheet(); }
    else if (action === 'sheet-pick') { ev.preventDefault(); st.sheet.pending = btn.dataset.srHandle || ''; renderSheet(); }
    else if (action === 'sheet-validate') { ev.preventDefault(); validateSheet(btn); }
    else if (action === 'alert-keep') { ev.preventDefault(); closeAlert(); }
    else if (action === 'alert-apply') { ev.preventDefault(); alertApply(btn); }
    else if (action === 'alert-deal') { ev.preventDefault(); btn.disabled = true; markAdded(btn); alertApply(btn, btn.dataset.srVariant, btn); }
  });
  document.addEventListener('click', (ev) => {
    if (st.sheet.open && ev.target instanceof Element && ev.target.closest('[data-sr-ov]')) { ev.preventDefault(); closeSheet(); }
    if (st.alert && ev.target instanceof Element && ev.target.closest('[data-sr-alert-ov]')) { ev.preventDefault(); closeAlert(); }
  });
  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape' && st.alert) { ev.preventDefault(); ev.stopPropagation(); closeAlert(); return; }
    if (ev.key === 'Escape' && st.sheet.open) { ev.preventDefault(); ev.stopPropagation(); closeSheet(); }
  }, true);

  document.addEventListener('cart:update', (ev) => {
    const src = ev.detail && ev.detail.data && ev.detail.data.source;
    if (src && src !== 'sr-cart') st.userAction = false;
    schedule();
  });
  /* sur window, en capture : passe avant l'écouteur délégué d'Horizon (on:submit, posé sur document en capture) */
  window.addEventListener('submit', (ev) => {
    const form = ev.target;
    if (!(form instanceof HTMLFormElement) || !form.closest('cart-drawer-component .sr-cart cart-discount-component, [data-sr-page] .sr-cart cart-discount-component')) return;
    ev.preventDefault();
    ev.stopImmediatePropagation();
    applyCode(form);
  }, true);
  document.addEventListener('discount:update', (ev) => { noteCodes(ev.detail && ev.detail.resource); schedule(); });

  function boot() {
    const c = component();
    if (!c) return false;
    if (c.dataset.srBound) return true;
    c.dataset.srBound = '1';
    new MutationObserver(() => { renderToast(); schedule(); }).observe(c, { childList: true, subtree: true, characterData: true });
    const d = q('cart-drawer-component dialog');
    if (d) new MutationObserver(() => { if (d.open) { st.last = null; afterRender(); fetchCodes(); } }).observe(d, { attributes: true, attributeFilter: ['open'] });
    afterRender();
    if (isOpen()) fetchCodes();
    return true;
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => { boot() || setTimeout(boot, 500); }, { once: true });
  else boot() || setTimeout(boot, 500);
  document.addEventListener('shopify:section:load', () => setTimeout(boot, 50));

  window.SRCart = { optimistic, add: addVariant, toast, refresh: refreshSilently, state: readState, openSheet, closeSheet, debug: () => ({ busy: st.busy, dirty: st.dirty, autoFixes: st.autoFixes, expiring: st.expiring, queue: st.queue.length, pending: st.pending.length, sheet: Object.assign({}, st.sheet), alert: st.alert ? { kind: st.alert.kind, code: st.alert.code, lost: st.alert.lost.map((l) => l.k), newS: st.alert.newS, rescue: !!st.alert.rescue } : null }) };
})();